import { EndConditionSettings } from '@paragon/contract-library';

export type GlobalEndConditionPattern =
  | 'blank'
  | 'topRibbon'
  | 'bottomRibbon'
  | 'topAndBottomRibbon'
  | 'doubleEndVertical'
  | 'topChordBearing'
  | 'topChordBearingWithBlock';

export function getSourcePreset(preset: EndConditionSettings): GlobalEndConditionPattern {
  // These fields are only accessible when advancedSettings === true
  if (
    preset.topChordCutBackDistance !== preset.topRibbonWidth ||
    preset.bottomChordCutBackDistance !==
      (preset.isTopChordBearing ? preset.bearingWidth + preset.bearingGap : preset.bottomRibbonWidth) ||
    preset.parapetHeight !== 0
  ) {
    // This indicates that all fields need to be shown via advancedSettings = true
    return 'blank';
  }

  if (preset.isTopChordBearing) {
    if (preset.bearingBlockWidth !== 0 && preset.bearingBlockElevation !== 0) {
      return 'topChordBearingWithBlock';
    }

    return 'topChordBearing';
  }

  const topRibbon = preset.topRibbonHeight !== 0 && preset.topRibbonWidth !== 0;
  const bottomRibbon = preset.bottomRibbonHeight !== 0 && preset.bottomRibbonWidth !== 0;

  if (topRibbon && bottomRibbon) {
    return 'topAndBottomRibbon';
  }

  if (topRibbon) {
    return 'topRibbon';
  }

  if (bottomRibbon) {
    return 'bottomRibbon';
  }

  return 'doubleEndVertical';
}

export const DEFAULT_END_CONDITION_PRESETS: Record<GlobalEndConditionPattern, Omit<EndConditionSettings, 'guid'>> = {
  blank: {
    endVerticalCount: 1,
    topChordSlider: false,
    fireCutDistance: 0,
    topRibbonWidth: 0,
    topRibbonHeight: 0,
    bottomRibbonWidth: 0,
    bottomRibbonHeight: 0,
    topChordCutBackDistance: 0,
    bottomChordCutBackDistance: 0,
    noBearing: false,
    bearingWidth: 3.5,
    bearingBlockWidth: 0,
    bearingBlockElevation: 0,
    measureBearingBlockFromTop: true,
    isTopChordBearing: false,
    bearingGap: 0,
    parapetHeight: 0,
    bearingType: 'DoublePlate',
    topChordSliderLength: 0,
  },
  topRibbon: {
    endVerticalCount: 1,
    topChordSlider: false,
    fireCutDistance: 0,
    topRibbonWidth: 1.5,
    topRibbonHeight: 3.5,
    bottomRibbonWidth: 0,
    bottomRibbonHeight: 0,
    topChordCutBackDistance: 1.5,
    bottomChordCutBackDistance: 0,
    noBearing: false,
    bearingWidth: 3.5,
    bearingBlockWidth: 0,
    bearingBlockElevation: 0,
    measureBearingBlockFromTop: true,
    isTopChordBearing: false,
    bearingGap: 0,
    parapetHeight: 0,
    bearingType: 'DoublePlate',
    topChordSliderLength: 0,
  },
  bottomRibbon: {
    endVerticalCount: 1,
    topChordSlider: false,
    fireCutDistance: 0,
    topRibbonWidth: 0,
    topRibbonHeight: 0,
    bottomRibbonWidth: 1.5,
    bottomRibbonHeight: 3.5,
    topChordCutBackDistance: 0,
    bottomChordCutBackDistance: 1.5,
    noBearing: false,
    bearingWidth: 3.5,
    bearingBlockWidth: 0,
    bearingBlockElevation: 0,
    measureBearingBlockFromTop: true,
    isTopChordBearing: false,
    bearingGap: 0,
    parapetHeight: 0,
    bearingType: 'DoublePlate',
    topChordSliderLength: 0,
  },
  topAndBottomRibbon: {
    endVerticalCount: 1,
    topChordSlider: false,
    fireCutDistance: 0,
    topRibbonWidth: 1.5,
    topRibbonHeight: 6,
    bottomRibbonWidth: 1.5,
    bottomRibbonHeight: 6,
    topChordCutBackDistance: 1.5,
    bottomChordCutBackDistance: 1.5,
    noBearing: false,
    bearingWidth: 3.5,
    bearingBlockWidth: 0,
    bearingBlockElevation: 0,
    measureBearingBlockFromTop: true,
    isTopChordBearing: false,
    bearingGap: 0,
    parapetHeight: 0,
    bearingType: 'DoublePlate',
    topChordSliderLength: 0,
  },
  doubleEndVertical: {
    endVerticalCount: 2,
    topChordSlider: false,
    fireCutDistance: 0,
    topRibbonWidth: 0,
    topRibbonHeight: 0,
    bottomRibbonWidth: 0,
    bottomRibbonHeight: 0,
    topChordCutBackDistance: 0,
    bottomChordCutBackDistance: 0,
    noBearing: false,
    bearingWidth: 3.5,
    bearingBlockWidth: 0,
    bearingBlockElevation: 0,
    measureBearingBlockFromTop: true,
    isTopChordBearing: false,
    bearingGap: 0,
    parapetHeight: 0,
    bearingType: 'DoublePlate',
    topChordSliderLength: 0,
  },
  topChordBearing: {
    endVerticalCount: 1,
    topChordSlider: true,
    topChordSliderLength: 24,
    fireCutDistance: 0,
    topRibbonWidth: 0,
    topRibbonHeight: 0,
    bottomRibbonWidth: 0,
    bottomRibbonHeight: 0,
    topChordCutBackDistance: 0,
    bottomChordCutBackDistance: 4,
    noBearing: false,
    bearingWidth: 3.5,
    bearingBlockWidth: 0,
    bearingBlockElevation: 0,
    measureBearingBlockFromTop: true,
    isTopChordBearing: true,
    bearingGap: 0.5,
    parapetHeight: 0,
    bearingType: 'DoublePlate',
  },
  topChordBearingWithBlock: {
    endVerticalCount: 1,
    topChordSlider: true,
    topChordSliderLength: 24,
    fireCutDistance: 0,
    topRibbonWidth: 0,
    topRibbonHeight: 0,
    bottomRibbonWidth: 0,
    bottomRibbonHeight: 0,
    topChordCutBackDistance: 0,
    bottomChordCutBackDistance: 3,
    noBearing: false,
    bearingWidth: 3,
    bearingBlockWidth: 3,
    bearingBlockElevation: 10,
    measureBearingBlockFromTop: true,
    isTopChordBearing: true,
    bearingGap: 0,
    parapetHeight: 0,
    bearingType: 'DoublePlate',
  },
};
